import { MapPin } from '@liveauctioneers/hammer-ui-core/icons';
import { BodyPrimary, BodySecondary, H1 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import TopBadge from '@/components/Badges/TopBadge';
import { useCatalogPurchaseProtectionEligible } from '@/components/PurchaseProtection/PurchaseProtectionBanner/hooks/useCatalogPurchaseProtectionEligible';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogIsDone,
    getCatalogIsTimedPlus,
    getCatalogLongAddress,
    getCatalogSellerId,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getSellerLogoId, getSellerName } from '@/redux/modules/seller/seller.selectors';
import { isTopRatedHouse } from '@/redux/modules/topRatedSellers';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { safelyRender } from '@/utils/safelyRender';
import { getAuctioneerLogoUrl, getSellerUrl } from '@/utils/urls';

import { CatalogDate } from '../Details/components/CatalogDate/CatalogDate';
import { AuctionCuratedBy } from './AuctionCuratedBy';
import { CatalogDetailsZoneMobileDescription } from './CatalogDetailsZoneMobileDescription';
import { CatalogDetailsZoneTimedPlusLink } from './CatalogDetailsZoneTimedPlusLink';
import { CatalogPurchaseProtectionLine } from './CatalogPurchaseProtectionLine';

const AUCTION_HOUSE_LOGO_SIZE_PX = 64;
const AUCTION_HOUSE_LOGO_IMG_SIZE_PX = 56;
const AUCTION_HOUSE_LOGO_SOURCE_SIZE_PX = 160;

type Props = {
    catalogId: number;
    className?: string;
    forceIsLive: boolean;
};

export const CatalogDetailsZone = ({ catalogId, className, forceIsLive }: Props) => {
    const formatMessage = useAppFormatMessage();
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const location = useAppSelector((state) => getCatalogLongAddress(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const sellerName = useAppSelector((state) => getSellerName(state, sellerId));
    const sellerIsTopRated = useAppSelector((state) => isTopRatedHouse(state, sellerId));
    const logoId = useAppSelector((state) => getSellerLogoId(state, sellerId));
    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const showSellerLogo = Boolean(logoId) && !whiteLabel;
    const sellerUrl = getSellerUrl(sellerId, sellerName);
    const isMobile = useIsMobileLayout();
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const purchaseProtectionEligible = useCatalogPurchaseProtectionEligible(catalogId);

    return (
        <div
            className={cn('mb-md flex flex-col gap-md bg-background-primary', className)}
            data-testid="catalog-details-zone"
        >
            <div className="flex flex-col gap-sm">
                <H1 data-testid="pageTitle">{safelyRender({ html: title })}</H1>
                <div className="flex items-center gap-xs">
                    {showSellerLogo && (
                        <RouteLink
                            className="flex shrink-0 items-center justify-center border border-border-secondary"
                            data-testid="details-zone-seller-logo"
                            style={{ height: AUCTION_HOUSE_LOGO_SIZE_PX, width: AUCTION_HOUSE_LOGO_SIZE_PX }}
                            to={sellerUrl}
                        >
                            <img
                                alt={sellerName}
                                className="object-contain"
                                height={AUCTION_HOUSE_LOGO_IMG_SIZE_PX}
                                src={getAuctioneerLogoUrl(logoId, AUCTION_HOUSE_LOGO_SOURCE_SIZE_PX)}
                                style={{
                                    maxHeight: AUCTION_HOUSE_LOGO_IMG_SIZE_PX,
                                    maxWidth: AUCTION_HOUSE_LOGO_IMG_SIZE_PX,
                                }}
                                width={AUCTION_HOUSE_LOGO_IMG_SIZE_PX}
                            />
                        </RouteLink>
                    )}
                    <div className="flex flex-col gap-2xs">
                        {sellerIsTopRated && <TopBadge iconSize="small" />}
                        <RouteLink
                            className="no-underline"
                            to={sellerUrl}
                        >
                            <BodyPrimary color="secondary">
                                {formatMessage(['catalogPage', 'catalogDetailsZone', 'by'], {
                                    values: {
                                        sellerName: (
                                            <span
                                                className="font-semibold text-text-primary"
                                                key="seller-name"
                                            >
                                                {sellerName}
                                            </span>
                                        ),
                                    },
                                })}
                            </BodyPrimary>
                        </RouteLink>
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-xs">
                <CatalogDate
                    catalogId={catalogId}
                    className="border-none pr-0"
                    forceIsLive={forceIsLive}
                    hideBorders
                    showAddToCalendar
                    showClosedLabel
                    timedPlusLink={!isCatalogDone && isTimedPlus ? <CatalogDetailsZoneTimedPlusLink /> : undefined}
                    variant="detailsZone"
                />
                <div className="flex items-center gap-xs">
                    <MapPin
                        color="primary"
                        size="sm"
                    />
                    <BodySecondary>{location}</BodySecondary>
                </div>
                <CatalogDetailsZoneMobileDescription catalogId={catalogId} />
                {!isMobile && purchaseProtectionEligible && <CatalogPurchaseProtectionLine />}
            </div>
            <AuctionCuratedBy catalogId={catalogId} />
        </div>
    );
};
